/**
 * Full-screen client picker (multi-client plan).
 *
 * Rendered by ClientProvider INSTEAD of the app when a DXC home user has no
 * active-client pick in this tab — a fresh tab always chooses explicitly,
 * so a deep link can never silently land in the wrong client's workspace.
 * The gate replaces children, not the route: after picking, the original
 * URL (e.g. /workshop/WS4) renders in place under the chosen client.
 *
 * Client-org users never see this — the BFF scopes them from their token and
 * the provider resolves them without a gate.
 */

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import { isZitadelMode } from "@/env";
import { DocsApiError, listClients, type ClientEntry } from "@/lib/docs-api";
import {
  storeClientPick,
  useActiveClient,
} from "@/lib/active-client";
import { listActiveSeedClients } from "@/lib/seed-clients";
import { ClientMark } from "./ClientMark";

export function ClientSelectGate() {
  const resolve = useActiveClient((s) => s.resolve);
  const zitadel = isZitadelMode();

  const { data, isLoading, isError, error, refetch, failureCount } = useQuery<
    ClientEntry[]
  >({
    queryKey: ["clients"],
    queryFn: listClients,
    enabled: zitadel,
    // A 4xx (no token, no grant) won't fix itself — fail straight to the
    // error panel. 5xx / network errors are usually a backend waking from
    // scale-to-zero: retry briefly at a flat 1s instead of the default
    // 3× exponential backoff, which kept "Loading clients…" up for ~7s more.
    retry: (count, err) =>
      count < 2 &&
      !(err instanceof DocsApiError && err.status >= 400 && err.status < 500),
    retryDelay: 1_000,
  });

  const clients: ClientEntry[] = zitadel
    ? (data ?? []).filter((c) => c.status === "active")
    : listActiveSeedClients();

  function pick(client: ClientEntry) {
    storeClientPick(client.id);
    resolve({ clientId: client.id, clientName: client.name, source: "picked" });
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center p-6"
      style={{ background: "var(--bg-deep)" }}
    >
      <SmokeCanvas
        className="pointer-events-none absolute inset-0 h-full w-full"
        cssVarName="--accent-true-blue"
        ariaLabel="Client workspace picker ambient smoke field"
      />

      <div
        className="panel-glow-mc relative z-10 w-full max-w-lg overflow-hidden"
        style={{
          background: "var(--bg-panel-alt)",
          borderRadius: 10,
        }}
      >
        <div
          className="flex flex-col gap-1 px-6 py-5"
          style={{ borderBottom: "1px solid hsl(var(--border))" }}
        >
          <p
            className="font-mono tracking-[0.14em] uppercase"
            style={{
              color: "var(--accent-true-blue)",
              fontSize: "var(--fs-label)",
            }}
          >
            Mission Control · Client workspace
          </p>
          <h1
            className="font-mono font-bold tracking-[0.12em] uppercase"
            style={{
              color: "var(--text-primary)",
              fontSize: "var(--fs-heading)",
            }}
          >
            Select client workspace
          </h1>
          <p
            className="mt-1 font-mono"
            style={{
              color: "var(--text-muted)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.04em",
              lineHeight: 1.5,
              maxWidth: "65ch",
            }}
          >
            Everything you view and edit applies to the selected client until
            you switch from the header. Each browser tab keeps its own
            selection.
          </p>
        </div>

        <div className="px-4 py-4">
          {zitadel && isLoading && (
            <div
              className="flex items-center gap-2 px-2 py-3 font-mono"
              style={{
                color: "var(--text-muted)",
                fontSize: "var(--fs-base)",
              }}
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {failureCount > 0 ? "Waking the workspace server…" : "Loading clients…"}
            </div>
          )}

          {zitadel && isError && (
            <div className="px-2 py-2 font-mono" style={{ fontSize: "var(--fs-base)" }}>
              <p style={{ color: "var(--accent-red, #c44)" }}>
                Couldn&apos;t load the client registry.
              </p>
              <p
                className="mt-2"
                style={{
                  color: "var(--text-muted)",
                  fontSize: "var(--fs-label)",
                  letterSpacing: "0.04em",
                  lineHeight: 1.5,
                }}
              >
                {error instanceof Error && /403|not permitted/i.test(error.message)
                  ? "Signed in as DXC, but this environment is still blocking non-facilitator client lists. A BFF update is required before Observer/Analyst/Engineer can pick a workspace."
                  : "Check your connection and try again. If this persists, sign out and back in so your role grant is on the token."}
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-3 rounded border px-3 py-1.5 font-mono uppercase tracking-widest"
                style={{
                  borderColor: "hsl(var(--border))",
                  color: "var(--text-secondary)",
                  fontSize: "var(--fs-label)",
                }}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && (
            <ul className="flex flex-col gap-2" role="listbox" aria-label="Client workspaces">
              {clients.map((client) => (
                <li key={client.id}>
                  <button
                    type="button"
                    role="option"
                    onClick={() => pick(client)}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left transition-colors"
                    style={{
                      border: "1px solid hsl(var(--border))",
                      background: "transparent",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "rgba(var(--accent-true-blue-rgb), 0.55)";
                      e.currentTarget.style.background =
                        "rgba(var(--accent-true-blue-rgb), 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "hsl(var(--border))";
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <ClientMark
                      name={client.name}
                      logoUrl={client.logoUrl}
                      size="lg"
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate font-mono font-semibold uppercase tracking-wide"
                        style={{
                          color: "var(--text-primary)",
                          fontSize: "var(--fs-base)",
                        }}
                      >
                        {client.name}
                      </span>
                      <span
                        className="mt-0.5 block truncate font-mono"
                        style={{
                          color: "var(--text-muted)",
                          fontSize: "var(--fs-label)",
                          letterSpacing: "0.1em",
                        }}
                      >
                        {client.id}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
              {clients.length === 0 && (
                <li
                  className="px-2 py-4 font-mono"
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "var(--fs-base)",
                    lineHeight: 1.5,
                  }}
                >
                  No active clients registered. Use Manage Crew → Add client,
                  then reload.
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
